// TESSERAX — RECORDS PERSONNELS PAR MODE (cœur pur, testé).
//
// Le jeu ne mémorisait qu'UN record global (`tesserax:best`). Conséquence : les modes
// courts orientés score-attack (Sprint / Ultra / Blitz) — pensés justement pour la
// session rejouable « bats ton meilleur » — ne déclenchaient quasiment JAMAIS la
// célébration « RECORD », car leur score plafonne bien en dessous d'une longue partie
// endless. Le crochet d'addiction propre à ces modes (« +X et je bats mon record »)
// était donc muet. On isole ici un registre de records PAR MODE : chaque mode garde sa
// propre meilleure marque, donc sa propre boucle d'auto-dépassement.
//
// Module PUR : ni THREE ni DOM, pas de Math.random, pas d'I/O. main.ts (via net/api)
// gère la persistance localStorage ; ici on ne fait que parser / comparer / sérialiser.
//
// Le socle « registre numérique tolérant » (parse défensif + meilleure valeur +
// sérialisation) est mutualisé dans core/numreg : record-PAR-MODE et sommet-PAR-DIMENSION
// (core/peaks) partageaient un code identique au caractère près. Ici on ne garde que la
// SÉMANTIQUE propre aux records de mode (types nommés, applyRecord, chasse au record).
import { type NumRegistry, parseNumReg, serializeNumReg, bestIn } from './numreg';

export type ModeRecords = NumRegistry;

// Parse tolérant d'un blob localStorage : JSON objet { mode: score } (cf. numreg).
export function parseRecords(raw: string | null | undefined): ModeRecords {
  return parseNumReg(raw);
}

export function serializeRecords(r: ModeRecords): string {
  return serializeNumReg(r);
}

// Meilleure marque connue pour un mode (0 si aucune).
export function bestFor(r: ModeRecords, mode: string): number {
  return bestIn(r, mode);
}

// Résultat d'une fin de partie pour un mode donné. PUR : ne mute pas l'entrée, renvoie
// un registre NEUF si amélioration. `isRecord` n'est vrai qu'à partir d'un score > 0 ET
// strictement supérieur à l'ancienne marque (égaler ne célèbre pas). `prev` = ancienne
// marque (0 si première), utile pour un « +N » lisible.
export interface RecordOutcome { records: ModeRecords; isRecord: boolean; prev: number; }
export function applyRecord(r: ModeRecords, mode: string, score: number): RecordOutcome {
  const prev = bestFor(r, mode);
  const s = Number.isFinite(score) ? Math.floor(score) : 0;
  if (s > 0 && s > prev) {
    return { records: { ...r, [mode]: s }, isRecord: true, prev };
  }
  return { records: r, isRecord: false, prev };
}

// CHASSE AU RECORD EN DIRECT (pur, testé). La célébration « RECORD » n'existait qu'à
// l'écran de fin : pendant la partie, le joueur ne voyait JAMAIS qu'il s'approchait de
// sa meilleure marque. C'est pourtant le crochet d'addiction « encore une » des modes
// score-attack (Sprint / Ultra / Blitz, mais aussi Free/Daily/Zen) — « plus que 240 et
// je bats mon record ». On décrit ici l'ÉTAT de chasse, à partir du score courant et de
// la marque à battre, pour que main.ts surface une pastille vivante + un apogée à l'instant
// pile du dépassement. PUR : fonction du seul score (aucun RNG) → déterminisme du défi intact.
//
//   · 'none'     — pas de cible (aucun record encore) OU encore loin du record.
//   · 'approach' — score dans la « zone chaude » (≥ near·record) mais sous le record.
//   · 'beaten'   — record dépassé (strictement) → l'instant à célébrer.
export type ChaseState = 'none' | 'approach' | 'beaten';
// Entre dans la zone chaude à 80 % du record : assez tôt pour créer la tension, assez tard
// pour ne pas l'afficher toute la partie (et la diluer).
export const CHASE_NEAR = 0.8;

export interface RecordChase {
  state: ChaseState;
  best: number;   // la marque à battre (0 si aucune)
  delta: number;  // approach : points NÉCESSAIRES pour battre (best−score+1) · beaten : points AU-DESSUS (score−best)
}
export function recordChase(score: number, best: number, near: number = CHASE_NEAR): RecordChase {
  const s = Number.isFinite(score) ? Math.max(0, Math.floor(score)) : 0;
  const b = Number.isFinite(best) && best > 0 ? Math.floor(best) : 0;
  if (b <= 0) return { state: 'none', best: 0, delta: 0 };
  if (s > b) return { state: 'beaten', best: b, delta: s - b };
  const n = Math.min(1, Math.max(0, near));
  const threshold = Math.floor(b * n);
  // On n'ouvre la chasse qu'avec un score > 0 (sinon « approach » dès la première frame
  // d'un record dérisoire) et au-dessus du seuil chaud.
  const state: ChaseState = s > 0 && s >= threshold ? 'approach' : 'none';
  return { state, best: b, delta: b - s + 1 };
}
